import { LearnSummaryDailyLimitView } from "#learn-summary-daily-limit-view";
import { LearnSummaryFreeTierCompleteView } from "#learn-summary-free-tier-complete-view";
import { LearnSummaryReadyView } from "#learn-summary-ready-view";
import { reviewFocusLabel } from "@-/dev.improve.haniverse.lib.learn.review-focus";
import { LearnSummaryWelcomeView } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.__views.welcome";
import { useLearnSummaryIsCurrent } from "#learn-summary-presence";
import { type LearnSummaryVariant, useLearnSummaryStart } from "#use-learn-summary-start";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { useEffect, useRef } from "react";
import {
  useLearnSummaryPageQueryOptions,
  useLearnSummaryResolutionSuspense,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";

/**
 * Rendered by `LearnSummaryPageSuspense` only once the local summary query has data, so reading it
 * here is a cache hit that never suspends and is typed as the resolution itself.
 */
export function LearnSummaryPageContent() {
  const localResolution = useLearnSummaryResolutionSuspense();
  // The content-validated resolution, once known for the current data, is the more precise one:
  // it skips due cards whose content this device can't open. The deck host observes the same query
  // for the card Start opens, so this adds no work.
  const validatedQueryOptions = useLearnSummaryPageQueryOptions({ validateContent: true });
  const { data: validatedResolution } = useQuery({ ...validatedQueryOptions, throwOnError: false });
  const resolution = validatedResolution ?? localResolution;
  const isCurrent = useLearnSummaryIsCurrent();
  const { dueReviewLabel, isPremium, pendingLabel, reviewFocus, start, startLabel, variant } =
    useLearnSummaryStart({ resolution, source: "summary" });
  useLearnSummaryViewedAnalytics(variant);

  if (resolution.isNewUser) {
    // The welcome opens the first slide. Beneath Learn's map (where the app opens) it waits for the
    // learner to come to the cards.
    return <LearnSummaryWelcomeView isActive={isCurrent} />;
  }

  const { dueReviewCardList } = resolution;

  // Every returning-user state is the SAME frame (see `LearnSummaryReadyView`); they differ only in
  // the CTA and whether there is a message. The exhausted-catalogue case used to return its own
  // full-screen view, which meant it dropped the layout entirely — now it keeps it and simply has
  // no button to press.
  const cta = startLabel === null ? null : { label: startLabel, onPress: start };

  // A FREE learner with no reviews left and no lesson left has finished the free tier outright —
  // the lesson resolver is capped at the free-tier levels for them, so an empty catalogue here
  // means the boundary, not the end of the curriculum. Congratulate them and make the case for
  // Premium instead of showing "Nothing to learn right now.", which reads like the app is done.
  // A PREMIUM learner reaching the same state really has exhausted the catalogue, so they keep
  // the original copy.
  // A review focus with nothing due has not finished anything: other kinds may be due and lessons
  // remain, so it points back to the Reviews menu instead of the free-tier or catalogue copy.
  //
  // A learner who has instead used up today's new-character allowance has NOT finished anything —
  // the resolver is simply withholding lessons until tomorrow. They only get here after reviewing
  // the cards that allowance introduced (the due scan revives them at the limit), so they keep the
  // usual summary card, which congratulates them and says what lifts the limit: the free tier's
  // cap offers the upgrade, and the learner's own Daily New Cards limit points to the setting.
  const dailyLimitReached =
    cta === null && reviewFocus === "all" ? resolution.dailyNewGraphemeLimitReached : null;
  if (dailyLimitReached !== null) {
    return (
      <LearnSummaryDailyLimitView
        dueReviewCardList={dueReviewCardList}
        dueReviewLabel={dueReviewLabel}
        limit={dailyLimitReached}
      />
    );
  }
  if (!isPremium && cta === null && reviewFocus === "all") {
    return <LearnSummaryFreeTierCompleteView />;
  }

  return (
    <LearnSummaryReadyView
      cta={cta}
      ctaPendingLabel={pendingLabel}
      dueReviewCardList={dueReviewCardList}
      dueReviewLabel={dueReviewLabel}
      message={
        cta !== null
          ? null
          : reviewFocus === "all"
            ? "Nothing to learn right now."
            : `Nothing due in ${reviewFocusLabel(reviewFocus)}. Switch Reviews to All in the menu to keep learning.`
      }
      showReviewOptions
    />
  );
}

/** One `learn_summary_viewed` each time the summary comes into view, or changes state in view. */
function useLearnSummaryViewedAnalytics(variant: LearnSummaryVariant | undefined): void {
  const isCurrent = useLearnSummaryIsCurrent();
  const viewedVariantRef = useRef<LearnSummaryVariant | null>(null);
  useEffect(() => {
    if (!isCurrent) {
      viewedVariantRef.current = null;
      return;
    }
    if (variant === undefined || viewedVariantRef.current === variant) {
      return;
    }
    viewedVariantRef.current = variant;
    captureAnalyticsEvent("learn_summary_viewed", { variant });
  }, [isCurrent, variant]);
}
